import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTER_LINKS } from '../../consts/app-keys.const';
import packageJson from '../../../../../package.json';
import { useHasFrameworkAccess } from '../../../sbframework/feature/use-sbframework-access';

interface ResourcesDropdownProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const ResourcesDropdown: React.FC<ResourcesDropdownProps> = ({
  isOpen,
  onOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  // Only holders see the framework column: it is invite-only, and a
  // menu entry for it would advertise a product to people who cannot
  // reach anything behind it.
  const { granted: frameworkGranted } = useHasFrameworkAccess();

  const touchposeVersion =
    (packageJson as { touchpose?: { version?: string } }).touchpose?.version ??
    t('management.product.unknownVersion');
  const eyeVersion =
    (packageJson as { squarebitEye?: { version?: string } }).squarebitEye?.version ??
    '';
  const frameworkVersion =
    (packageJson as { sbframework?: { version?: string } }).sbframework?.version ??
    t('management.product.unknownVersion');
  const subdivsVersion =
    (packageJson as { squarebitSubdivs?: { version?: string } }).squarebitSubdivs
      ?.version ?? t('management.product.unknownVersion');

  // To add a new product column, add an entry to this array.
  const PRODUCTS: Array<{
    key: string;
    label: string;
    labelColor: string;
    links: Array<{ label: string; description: string; to: string }>;
  }> = [
    {
      key: 'touchpose',
      label: 'TOUCHPOSE',
      labelColor: 'var(--color-brand-link)',
      links: [
        {
          label: t('resourcesDropdown.docs'),
          description: t('resourcesDropdown.docsDescription'),
          to: ROUTER_LINKS.TOUCHPOSE_DOCS
        },
        {
          label: t('resourcesDropdown.changelog'),
          description: t('resourcesDropdown.changelogDescription', {
            version: touchposeVersion
          }),
          to: ROUTER_LINKS.TOUCHPOSE_CHANGELOG
        },
        {
          label: t('resourcesDropdown.roadmap'),
          description: t('resourcesDropdown.roadmapDescription'),
          to: ROUTER_LINKS.TOUCHPOSE_ROADMAP
        },
        {
          label: t('resourcesDropdown.faq'),
          description: t('resourcesDropdown.faqDescription'),
          to: ROUTER_LINKS.TOUCHPOSE_FAQ
        }
      ]
    },
    {
      key: 'squarebit-subdivs',
      label: 'SQUAREBIT SUBDIVS',
      labelColor: 'var(--color-brand-link)',
      links: [
        {
          label: t('resourcesDropdown.docs'),
          description: t('resourcesDropdown.subdivsDocsDescription'),
          to: ROUTER_LINKS.SQUAREBIT_SUBDIVS_DOCS
        },
        {
          label: t('resourcesDropdown.changelog'),
          description: t('resourcesDropdown.latestUpdates', {
            version: subdivsVersion
          }),
          to: ROUTER_LINKS.SQUAREBIT_SUBDIVS_CHANGELOG
        }
      ]
    },
    {
      key: 'squarebit-eye',
      label: 'SQUAREBIT EYE',
      labelColor: 'var(--color-brand-link)',
      links: [
        {
          label: t('resourcesDropdown.docs'),
          description: t('resourcesDropdown.eyeDocsDescription'),
          to: ROUTER_LINKS.SQUAREBIT_EYE_DOCS
        },
        {
          label: t('resourcesDropdown.changelog'),
          description: t('resourcesDropdown.latestUpdates', {
            version: eyeVersion
          }),
          to: ROUTER_LINKS.SQUAREBIT_EYE_CHANGELOG
        }
      ]
    }
  ];

  // Appended rather than declared inline, so the column simply is not there
  // for an account without the grant - the framework is invite-only, and a
  // menu entry leading to a 404 is worse than no entry.
  if (frameworkGranted) {
    PRODUCTS.push({
      key: 'sbframework',
      label: 'SQUAREBIT FRAMEWORK',
      labelColor: 'var(--color-brand-link)',
      links: [
        {
          label: t('resourcesDropdown.docs'),
          description: t('resourcesDropdown.frameworkDocsDescription'),
          to: ROUTER_LINKS.SBFRAMEWORK_DOCS
        },
        {
          label: t('resourcesDropdown.apiReference'),
          description: t('resourcesDropdown.apiReferenceDescription'),
          to: ROUTER_LINKS.SBFRAMEWORK_API
        },
        {
          label: t('resourcesDropdown.changelog'),
          description: t('resourcesDropdown.latestUpdates', {
            version: frameworkVersion
          }),
          to: ROUTER_LINKS.SBFRAMEWORK_CHANGELOG
        }
      ]
    });
  }

  const handleLinkClick = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        onClick={() => (isOpen ? onClose() : onOpen())}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`font-body text-medium rounded-full px-[16px] py-[7px] border-none cursor-pointer flex items-center gap-[4px] transition-all duration-150 ${isOpen || isHovered ? 'text-blue bg-surface-hover' : 'text-base-black bg-transparent'}`}
      >
        {t('header.resources')}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown shell */}
      {isOpen && (
        <div className="absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 z-[200]">
          <div className="animate-dropdown min-w-[560px] bg-surface-base border-[1.5px] border-surface-hover rounded-[16px] p-[10px] shadow-[0_8px_32px_rgba(34,211,238,0.13),0_2px_8px_rgba(0,0,0,0.07)] font-body">
            {/* Product columns */}
            <div className="flex gap-[8px]">
              {PRODUCTS.map((product, productIndex) => (
                <React.Fragment key={product.key}>
                  {productIndex > 0 && (
                    <div
                      onMouseEnter={() => setHoveredCard(product.key)}
                      onMouseLeave={() => setHoveredCard(null)}
                      className={`w-[1px] my-[4px] ${hoveredCard === product.key ? 'bg-surface-card-hover' : 'bg-surface-secondary'}`}
                    />
                  )}
                  <div className="flex-1">
                    <div
                      className="font-body text-[10px] font-bold tracking-[0.08em] pt-[2px] px-[6px] pb-[8px]"
                      style={{ color: product.labelColor }}
                    >
                      {product.label}
                    </div>
                    {product.links.map((link) => (
                      <div
                        key={link.label}
                        onClick={() => handleLinkClick(link.to)}
                        className="px-[10px] py-[8px] rounded-[8px] cursor-pointer hover:bg-surface-hover"
                      >
                        <div className="font-body text-[13px] font-semibold text-content-primary">
                          {link.label}
                        </div>
                        <div className="font-body text-[11px] font-regular text-slate-500 mt-[1px]">
                          {link.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Discord footer */}
            <div className="border-t border-surface-secondary mt-[8px] pt-[6px]">
              <a
                href="https://discord.gg/u3rpHEg6Ct"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onClose()}
                className="flex items-center gap-[10px] px-[10px] py-[8px] rounded-[8px] no-underline hover:bg-surface-hover"
              >
                <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                  <path
                    d="M15.25 1.25A14.9 14.9 0 0 0 11.5.25a.06.06 0 0 0-.06.03c-.16.28-.34.65-.47.94a13.76 13.76 0 0 0-3.94 0 9.5 9.5 0 0 0-.47-.94.06.06 0 0 0-.06-.03A14.86 14.86 0 0 0 2.75 1.25a.06.06 0 0 0-.03.02C.4 4.85-.24 8.33.07 11.77a.07.07 0 0 0 .03.04 14.97 14.97 0 0 0 4.47 2.19.06.06 0 0 0 .07-.02c.34-.46.65-.94.91-1.45a.06.06 0 0 0-.03-.08 9.86 9.86 0 0 1-1.4-.65.06.06 0 0 1-.01-.1c.1-.07.19-.14.28-.22a.06.06 0 0 1 .06-.01c2.93 1.3 6.1 1.3 9 0a.06.06 0 0 1 .06.01c.09.07.19.15.28.22a.06.06 0 0 1-.01.1c-.45.26-.91.48-1.4.65a.06.06 0 0 0-.03.08c.27.5.58.99.91 1.45a.06.06 0 0 0 .07.02 14.93 14.93 0 0 0 4.48-2.19.06.06 0 0 0 .03-.04c.37-3.73-.62-6.97-2.65-9.5a.05.05 0 0 0-.03-.02ZM6.01 9.75c-.85 0-1.55-.76-1.55-1.69 0-.94.69-1.7 1.55-1.7.87 0 1.56.77 1.55 1.7 0 .93-.69 1.69-1.55 1.69Zm5.74 0c-.85 0-1.55-.76-1.55-1.69 0-.94.69-1.7 1.55-1.7.87 0 1.56.77 1.55 1.7 0 .93-.68 1.69-1.55 1.69Z"
                    fill="#5865F2"
                  />
                </svg>
                <div>
                  <div className="font-body text-[13px] font-semibold text-content-primary">
                    {t('resourcesDropdown.discord')}
                  </div>
                  <div className="font-body text-[11px] font-regular text-slate-500 mt-[1px]">
                    {t('resourcesDropdown.discordDescription')}
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesDropdown;
